import React, { useState, useEffect, useCallback } from 'react';
import { Camera, Calendar, Clock, FilePenLine, Phone, Mail, MapPin, Trash2, MessageSquare, Send } from 'lucide-react';
import ModalEditarAviso from '../componentes/ModalEditarAviso';
import ModalEnviarDocumentos from '../componentes/ModalEnviarDocumentos';
import ModalVerImagenes from '../componentes/ModalVerImagenes';
import { getEstadoBadge } from '../utilidades/estadoUtils';
import { API_URL, eliminarAviso } from '../servicios/api';

const AvisosPendientes = () => {
  const [avisos, setAvisos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);
  const [avisoSeleccionado, setAvisoSeleccionado] = useState(null);
  const [fechaDesde, setFechaDesde] = useState(''); // New state
  const [fechaHasta, setFechaHasta] = useState(''); // New state
  const [tecnicoGlobalId, setTecnicoGlobalId] = useState(localStorage.getItem('filtro_tecnico_id') || '');
  const [isImagenesModalOpen, setIsImagenesModalOpen] = useState(false);
  const [imagenesSeleccionadas, setImagenesSeleccionadas] = useState([]);

  // Función auxiliar para formatear nombres de documentos
  const formatDocumentName = (docName) => {
    switch (docName) {
      case 'factura': return 'Factura';
      case 'albaran': return 'Albarán';
      case 'informe_tecnico': return 'Informe Técnico';
      case 'presupuesto': return 'Presupuesto';
      default: return docName.charAt(0).toUpperCase() + docName.slice(1).replace(/_/g, ' ');
    }
  };

  const obtenerAvisos = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('No autenticado. Por favor, inicia sesión.');
      setCargando(false);
      return;
    }

    try {
      const params = new URLSearchParams({ estado: 'pendiente,en_diagnostico,esperando_repuesto,presupuestado' });
      if (fechaDesde) params.set('fecha_desde', fechaDesde);
      if (fechaHasta) params.set('fecha_hasta', fechaHasta);
      const usuarioActual = JSON.parse(localStorage.getItem('usuario') || 'null');
      if (usuarioActual?.rol === 'administrador' && tecnicoGlobalId) params.set('tecnico_id', tecnicoGlobalId);
      const url = `${API_URL}/obtener_avisos.php?${params.toString()}`;

      const respuesta = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const datos = await respuesta.json();

      // El backend ahora devuelve directamente el array de avisos, no un objeto con 'exito'
      const avisosRecibidos = datos.exito && Array.isArray(datos.avisos) ? datos.avisos : []; // Asegurarse de que sea un array

      const avisosConImagenes = await Promise.all(avisosRecibidos.map(async (aviso) => {
        const imagenesRes = await fetch(`${API_URL}/obtener_imagenes_aviso.php?id_aviso=${aviso.id_aviso}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        const imagenesData = await imagenesRes.json();
        return { ...aviso, imagenes: imagenesData.exito ? imagenesData.imagenes : [] };
      }));
      setAvisos(avisosConImagenes);
      // No hay 'else' aquí porque el backend devuelve un array vacío si no hay avisos
      if (avisosRecibidos.length === 0) {
        setError('No se encontraron avisos pendientes con los filtros seleccionados.'); // O un mensaje más específico si el backend lo envía
      } else {
        setError(null); // Clear any previous error
      }
    } catch (err) {
      console.error('Error al obtener avisos pendientes:', err);
      setError('Error de conexión con el servidor al obtener avisos pendientes.');
    } finally {
      setCargando(false);
    }
  }, [fechaDesde, fechaHasta, tecnicoGlobalId]); // Dependencies for useCallback

  useEffect(() => {
    obtenerAvisos();
  }, [obtenerAvisos]); // Dependency for useEffect

  useEffect(() => {
    const onTecnicoGlobalChange = (event) => setTecnicoGlobalId(event.detail?.tecnicoId || '');
    window.addEventListener('filtro-tecnico-cambiado', onTecnicoGlobalChange);
    return () => window.removeEventListener('filtro-tecnico-cambiado', onTecnicoGlobalChange);
  }, []);

  const handleEditClick = (aviso) => {
    setAvisoSeleccionado(aviso);
    setIsEditModalOpen(true);
  };

  const handleDocsClick = (aviso) => {
    setAvisoSeleccionado(aviso);
    setIsDocsModalOpen(true);
  };

  const handleCloseModals = () => {
    setIsEditModalOpen(false);
    setIsDocsModalOpen(false);
    setAvisoSeleccionado(null);
  };

  const handleAvisoUpdated = () => {
    obtenerAvisos();
  };

  const handleDeleteClick = async (id_aviso) => {
    if (!window.confirm('¿Eliminar definitivamente este servicio? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const datos = await eliminarAviso(id_aviso);
      if (datos.exito) {
        if (datos.mensaje) window.alert(datos.mensaje);
        await obtenerAvisos();
      } else {
        window.alert(`Error al eliminar servicio: ${datos.mensaje}`);
      }
    } catch (err) {
      console.error('Error al eliminar aviso:', err);
      window.alert(err.response?.data?.mensaje || 'Error de conexión con el servidor al eliminar el servicio.');
    }
  };

  const handleVerImagenesClick = (imagenes) => {
    setImagenesSeleccionadas(imagenes);
    setIsImagenesModalOpen(true);
  };

  const handleCloseImagenesModal = () => {
    setIsImagenesModalOpen(false);
    setImagenesSeleccionadas([]);
  };

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Avisos Pendientes</h1>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <label htmlFor="fechaDesde" className="text-sm font-medium text-gray-700">Desde:</label>
          <input
            type="date"
            id="fechaDesde"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
          />
          <label htmlFor="fechaHasta" className="text-sm font-medium text-gray-700">Hasta:</label>
          <input
            type="date"
            id="fechaHasta"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
          />
          <button
            onClick={obtenerAvisos} // Trigger fetch on button click
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Filtrar
          </button>
        </div>
      </div>

      {/* Table for larger screens */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[1000px] border-separate border-spacing-y-2">
          <thead className="bg-slate-900 text-sm text-slate-400">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Cliente</th>
              <th className="px-4 py-3 text-left font-semibold">Contacto</th>
              <th className="px-4 py-3 text-left font-semibold">Detalles del Aviso</th>
              <th className="px-4 py-3 text-left font-semibold">Estado</th>
              <th className="px-4 py-3 text-left font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr>
                <td colSpan="5" className="px-4 py-10 text-center text-slate-400">Cargando...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="5" className="px-4 py-10 text-center text-red-400">Error: {error}</td>
              </tr>
            ) : avisos.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-10 text-center text-slate-400">
                  No hay avisos pendientes con los filtros seleccionados.
                </td>
              </tr>
            ) : (
              avisos.map((aviso) => (
                <tr key={aviso.id_aviso} className="bg-slate-800 hover:bg-slate-700 transition-colors rounded-lg">
                  <td className="px-4 py-4 align-top rounded-l-lg">
                    <div className="font-medium text-slate-100">{aviso.nombre_cliente}</div>
                    <div className="text-sm text-slate-400 truncate max-w-xs">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(aviso.direccion)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-blue-400 transition-colors"
                      >
                        {aviso.direccion}
                      </a>
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <a href={`tel:${aviso.telefono}`} className="hover:text-blue-400 transition-colors">
                        {aviso.telefono}
                      </a>
                      <a href={`https://wa.me/${(aviso.telefono ?? '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-400 transition-colors">
                        <MessageSquare className="w-4 h-4" />
                      </a>
                    </div>
                    <div className="text-sm text-blue-400 hover:text-blue-300 truncate max-w-xs">
                      <a href={`mailto:${aviso.email_cliente}`}>{aviso.email_cliente}</a>
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="text-sm text-slate-300">
                      {new Date(aviso.fecha_programada).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      {' - '}
                      {new Date(aviso.fecha_programada).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className="text-sm text-slate-400">{aviso.descripcion}</div>
                  </td>
                  <td className="px-4 py-4 align-top">
                    {getEstadoBadge(aviso.estado)}
                  </td>
                  <td className="px-4 py-4 align-top rounded-r-lg">
                    <div className="flex items-center gap-2">
                      {aviso.imagenes && aviso.imagenes.length > 0 && (
                        <button className="p-1.5 text-blue-400 hover:text-white hover:bg-blue-700 rounded-md transition-colors" onClick={() => handleVerImagenesClick(aviso.imagenes)}><Camera className="w-5 h-5" /></button>
                      )}
                      <button
                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-md transition-colors"
                        onClick={() => handleEditClick(aviso)}
                      >
                        <FilePenLine className="w-5 h-5" />
                      </button>
                      <button className="p-1.5 text-red-400 hover:text-white hover:bg-red-700 rounded-md transition-colors"
                        onClick={() => handleDeleteClick(aviso.id_aviso)}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cards for smaller screens */}
      <div className="md:hidden grid grid-cols-1 gap-4 mx-auto max-w-md">
        {cargando ? (
          <p className="text-slate-400 text-center mt-4">Cargando...</p>
        ) : error ? (
          <p className="text-red-400 text-center mt-4">Error: {error}</p>
        ) : avisos.length === 0 ? (
          <p className="text-slate-400 text-center mt-4">No hay avisos pendientes.</p>
        ) : (
          avisos.map((aviso) => (
            <div key={aviso.id_aviso} className="bg-slate-800 p-4 rounded-lg shadow-md">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-slate-100 text-lg truncate">{aviso.nombre_cliente}</h3>
                {getEstadoBadge(aviso.estado)}
              </div>
              <div className="text-sm text-white mb-1 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="truncate">
                  {new Date(aviso.fecha_programada).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  {' - '}
                  {new Date(aviso.fecha_programada).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="text-sm text-white mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-400" />
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(aviso.direccion)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-blue-400 transition-colors truncate"
                >
                  {aviso.direccion}
                </a>
              </div>
              <p className="text-sm text-slate-300 mb-3 truncate">{aviso.descripcion}</p>

              {aviso.documentos_a_enviar && JSON.parse(aviso.documentos_a_enviar).length > 0 && (
                <div className="mt-2">
                  <span className="font-medium text-slate-300">Documentos a Enviar:</span>
                  <ul className="list-disc list-inside text-sm text-slate-400 mt-1">
                    {JSON.parse(aviso.documentos_a_enviar).map((doc, index) => (
                      <li key={index}>{formatDocumentName(doc)}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border-t border-slate-700 pt-3 mt-3">
                <div className="flex items-center gap-2 text-sm text-white mb-1">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <a href={`tel:${aviso.telefono}`} className="hover:text-blue-400 transition-colors truncate">
                    {aviso.telefono}
                  </a>
                  <a href={`https://wa.me/${(aviso.telefono ?? '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-400 transition-colors">
                    <MessageSquare className="w-4 h-4" />
                  </a>
                </div>
                <div className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300">
                  <Mail className="w-4 h-4 text-white" />
                  <a href={`mailto:${aviso.email_cliente}`} className="truncate">{aviso.email_cliente}</a>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                {aviso.imagenes && aviso.imagenes.length > 0 && (
                  <button className="p-2 text-blue-400 hover:text-white hover:bg-blue-700 rounded-md transition-colors" onClick={() => handleVerImagenesClick(aviso.imagenes)}><Camera className="w-5 h-5" /></button>
                )}
                <button
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-md transition-colors"
                  onClick={() => handleEditClick(aviso)}
                >
                  <FilePenLine className="w-5 h-5" />
                </button>
                <button className="p-2 text-red-400 hover:text-white hover:bg-red-700 rounded-md transition-colors">
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {avisoSeleccionado && (
        <ModalEditarAviso
          isOpen={isEditModalOpen}
          onClose={handleCloseModals}
          aviso={avisoSeleccionado}
          onUpdateAviso={handleAvisoUpdated}
        />
      )}

      {avisoSeleccionado && (
        <ModalEnviarDocumentos
          isOpen={isDocsModalOpen}
          onClose={handleCloseModals}
          aviso={avisoSeleccionado}
          onUpdateAviso={handleAvisoUpdated}
        />
      )}

      <ModalVerImagenes
        isOpen={isImagenesModalOpen}
        onClose={handleCloseImagenesModal}
        imagenes={imagenesSeleccionadas}
      />
    </div>
  );
};

export default AvisosPendientes;
